// @ts-nocheck
import { useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { 
  recordAffiliateEarning,
  getReferredAffiliateIdForUser
} from '@/utils/affiliateTracking';
import { invokeEdgeFunction } from "@/lib/invokeEdgeFunction";

interface PurchaseMembershipParams {
  membership_slug?: string;
  billing_type?: string;
  price_cents?: number;
  stripe_price_id?: string;
  is_addon?: boolean;
  membership_id?: string;
  price_id?: string;
  success_url?: string;
  cancel_url?: string;
  audience_type?: string;
  source?: string;
  coupon_code?: string | null;
  payment_plan_id?: string;
}

interface AffiliateData {
  affiliateId: string;
  commissionRate: number;
}

export const usePurchaseMembership = () => {
  const { user, refreshProfile } = useAuth();
  const [isProcessing, setIsProcessing] = useState(false);

  // ✅ LOG: Hook initialization
  console.log('🔧 [PURCHASE-HOOK] usePurchaseMembership hook initialized:', {
    hasUser: !!user,
    userId: user?.id,
    userEmail: user?.email,
    timestamp: new Date().toISOString()
  });

  // Helper: always use lifetime attribution from profile
  const getAffiliateData = async (): Promise<AffiliateData | null> => {
    if (!user?.id) return null;

    try {
      const referredAffiliateId = await getReferredAffiliateIdForUser(user.id);
      if (!referredAffiliateId) {
        console.log('ℹ️ [PURCHASE-HOOK] No referred_by_affiliate_id on profile');
        return null;
      }

      const { data: affiliateData, error: affiliateError } = await supabase
        .from('affiliates')
        .select('id, commission_rate, status')
        .eq('id', referredAffiliateId)
        .eq('status', 'active')
        .maybeSingle();

      if (affiliateError || !affiliateData) {
        console.log('⚠️ [PURCHASE-HOOK] Referred affiliate not found or inactive:', affiliateError?.message);
        return null;
      }

      return {
        affiliateId: affiliateData.id,
        commissionRate: affiliateData.commission_rate
      };
    } catch (error) {
      console.error('❌ [PURCHASE-HOOK] Error fetching lifetime affiliate attribution:', error);
      return null;
    }
  };

  const purchaseMembership = async (params: PurchaseMembershipParams) => {
    const { membership_slug, billing_type, price_cents, stripe_price_id, is_addon, audience_type, source, coupon_code } = params;

    // ✅ LOG: Hook function called
    console.log('🔧 [PURCHASE-HOOK] purchaseMembership function called with params:', {
      params,
      hasUser: !!user,
      userId: user?.id,
      userEmail: user?.email,
      timestamp: new Date().toISOString()
    });

    if (!user) {
      console.error('❌ [PURCHASE-HOOK] No user available for membership purchase');
      toast.error('Please log in to purchase a membership');
      return;
    }

    // For new flexible usage, allow either the old way or new way
    if (!membership_slug && !params.membership_id) {
      console.error('❌ [PURCHASE-HOOK] Invalid membership information:', {
        membershipSlug: membership_slug,
        membershipId: params.membership_id,
        allParams: params
      });
      toast.error('Invalid membership information');
      return;
    }

    // ✅ UPDATED: Allow dynamic price creation when no stripe_price_id is provided
    // Only validate price_cents is present for dynamic price creation
    if (!stripe_price_id && !params.price_id && !price_cents) {
      console.error('❌ [PURCHASE-HOOK] Missing pricing information for dynamic price creation:', {
        stripe_price_id,
        price_id: params.price_id,
        price_cents,
        allParams: params
      });
      toast.error('Payment configuration error: Missing pricing information');
      return;
    }

    if (!price_cents && !params.price_id) {
      console.error('❌ [PURCHASE-HOOK] Invalid pricing information:', {
        priceCents: price_cents,
        priceId: params.price_id,
        allParams: params
      });
      toast.error('Invalid pricing information');
      return;
    }

    setIsProcessing(true);

    try {
      // Use coupon from params first, then fall back to session storage
      const appliedCouponCode = coupon_code || sessionStorage.getItem('applied_coupon_code');
      const appliedCouponAffiliateId = sessionStorage.getItem('applied_coupon_affiliate_id');

      // ✅ LOG: Right before calling Stripe edge function
      const edgeFunctionPayload = {
        membership_slug,
        billing_type,
        price_cents,
        stripe_price_id,
        is_addon,
        audience_type,
        source,
        coupon_code: appliedCouponCode,
        ...params // Spread all params to include the new optional fields
      };

      console.log('🔧 [PURCHASE-HOOK] Creating Stripe checkout session for membership:', {
        membershipSlug: membership_slug || params.membership_id,
        userId: user.id,
        userEmail: user.email,
        audienceType: audience_type,
        source: source,
        isAddon: is_addon,
        stripe_price_id: stripe_price_id,
        allParams: params,
        timestamp: new Date().toISOString()
      });

      console.log('📋 [PURCHASE-HOOK] Full edge function payload being sent:', {
        fullPayload: edgeFunctionPayload,
        timestamp: new Date().toISOString()
      });

      // Call the edge function to create Stripe checkout session
      const { data, error } = await invokeEdgeFunction('create-membership-checkout', {
        body: edgeFunctionPayload
      });

      if (error) {
        console.error('❌ [PURCHASE-HOOK] Stripe checkout session creation failed:', {
          error,
          payload: edgeFunctionPayload,
          timestamp: new Date().toISOString()
        });
        throw error;
      }

      if (!data?.url) {
        console.error('❌ [PURCHASE-HOOK] No checkout URL returned from Stripe:', {
          data,
          payload: edgeFunctionPayload,
          timestamp: new Date().toISOString()
        });
        toast.error('Failed to create checkout session');
        return;
      }

      console.log('✅ [PURCHASE-HOOK] Stripe checkout session created successfully:', {
        checkoutUrl: data.url,
        sessionId: data.sessionId,
        timestamp: new Date().toISOString()
      });

// Persist purchase context locally to attribute commission after successful payment
try {
  if (data.sessionId && price_cents) {
    localStorage.setItem(
      `membership_purchase_${data.sessionId}`,
      JSON.stringify({
        price_cents,
        membership_slug: membership_slug || params.membership_id || null,
        timestamp: new Date().toISOString()
      })
    );
    console.log('🧾 [PURCHASE-HOOK] Stored purchase context for session:', data.sessionId);
  } else {
    console.log('ℹ️ [PURCHASE-HOOK] No sessionId or price_cents to persist purchase context');
  }
} catch (e) {
  console.warn('⚠️ [PURCHASE-HOOK] Failed to persist purchase context:', e);
}

      // Redirect to Stripe checkout
      console.log('🔧 [PURCHASE-HOOK] Redirecting to Stripe checkout:', {
        redirectUrl: data.url,
        timestamp: new Date().toISOString()
      });
      
      window.location.href = data.url;

    } catch (error: any) {
      console.error('❌ [PURCHASE-HOOK] Membership purchase failed:', {
        error: error.message || error,
        errorName: error.name,
        errorStack: error.stack,
        params,
        userId: user.id,
        userEmail: user.email,
        timestamp: new Date().toISOString()
      });
      
      // Show detailed error to user
      const errorMessage = error.message || 'Failed to initiate purchase';
      toast.error(`Payment Error: ${errorMessage}. Please contact support with this error.`);
      
      // Send immediate error alert email
      invokeEdgeFunction('send-error-alert', {
        body: {
          error_type: 'Membership Purchase Failed',
          error_message: errorMessage,
          user_email: user.email,
          user_id: user.id,
          metadata: {
            membership_slug: params.membership_slug,
            membership_id: params.membership_id,
            error_stack: error.stack,
            timestamp: new Date().toISOString()
          }
        }
      }).catch(alertError => {
        console.error('Failed to send error alert:', alertError);
      });
      
      // Log to console for admin visibility
      console.error('🚨 PAYMENT FAILURE - User Impact:', {
        user: user.email,
        membership: params.membership_slug,
        error: errorMessage,
        fullError: error
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleStripeSuccess = async (sessionId: string) => {
    console.log('🎉 [PURCHASE-HOOK] Processing Stripe success:', {
      sessionId,
      userId: user?.id,
      userEmail: user?.email,
      timestamp: new Date().toISOString()
    });

    if (!user) {
      console.error('❌ [PURCHASE-HOOK] No user available for success processing');
      throw new Error('User not authenticated');
    }

    try {
      // update-membership-from-stripe was never deployed; membership activation
      // is handled by the Stripe webhook, not from the browser.

      // Post-payment affiliate earnings attribution
      try {
        const storedStr = localStorage.getItem(`membership_purchase_${sessionId}`);
        if (storedStr) {
          const stored: { price_cents?: number; membership_slug?: string | null } = JSON.parse(storedStr);
          const affiliateData = await getAffiliateData();
          if (affiliateData && stored.price_cents && stored.price_cents > 0) {
            const commissionAmount = (stored.price_cents * affiliateData.commissionRate) / 100 / 100; // cents -> dollars
            await recordAffiliateEarning({
              affiliate_id: affiliateData.affiliateId,
              amount: commissionAmount,
              commission_rate: affiliateData.commissionRate,
              source_type: 'membership_purchase',
              source_id: (data && (data.profile_membership_id || data.membership_id || null)) ?? null,
              status: 'pending'
            });
            console.log('✅ [PURCHASE-HOOK] Affiliate earning recorded post-payment:', {
              affiliateId: affiliateData.affiliateId,
              commissionAmount,
              commissionRate: affiliateData.commissionRate,
              sourceId: data?.profile_membership_id || data?.membership_id || null,
            });
          } else {
            console.log('ℹ️ [PURCHASE-HOOK] No affiliate data or price found for post-payment earning');
          }
          localStorage.removeItem(`membership_purchase_${sessionId}`);
        } else {
          console.log('ℹ️ [PURCHASE-HOOK] No stored purchase context found for session:', sessionId);
        }
      } catch (e) {
        console.warn('⚠️ [PURCHASE-HOOK] Failed to record post-payment affiliate earning:', e);
      }

      // Refresh the user profile to get updated membership info
      console.log('🔄 [PURCHASE-HOOK] Refreshing profile after successful membership update...');
      await refreshProfile();

      console.log('🎉 [PURCHASE-HOOK] Stripe success processing completed successfully');

    } catch (error: any) {
      console.error('❌ [PURCHASE-HOOK] Failed to process Stripe success:', {
        error: error.message || error,
        sessionId,
        userId: user.id,
        timestamp: new Date().toISOString()
      });
      throw error;
    }
  };

  return {
    purchaseMembership,
    handleStripeSuccess,
    isProcessing
  };
};
